import clsx from "clsx";
import React, {
  FC,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import useGetDealerAddress from "~/components/Forms/DealerPassportForm2/hook/useGetDealerAddress";
import useUpdateAddress from "~/components/Forms/DealerPassportForm2/hook/useUpdateAddress";
import Button from "~/shared/ui/Fields/Button";
import { resolveIsHouseFieldComplete } from "~/components/Forms/DealerPassportForm2/Address/config/resolver";
import {
  getDealerCoordinats,
  getDealerFields,
  getDealerInfoAsp,
  getDealerInfoLegal,
  getDealerInfoMail,
  getDealerXshop,
} from "~/components/Forms/DealerPassportForm2/Address/options";
import {
  CoordinatesContextProps,
  GetCoordinatesContext,
} from "~/components/Forms/DealerPassportForm2/getCoordinates";
import { NOTIFICATIONS } from "~/constans/notifications";
import { useAddNotificationMutation } from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";

import AdditionalAddress from "./Components/AdditionalAddress";
import AspAddress from "./Components/AspAddress";
import LadaAddress from "./Components/LadaAddress";
import MainAddress from "./Components/MainAddress";
import NetxAddress from "./Components/NetxAddress";
import {
  addressFields,
  permissionMap,
  permissionSaveButton,
  transformData,
} from "./helper";

import s from "./Address.less";

interface AddressProps {
  DealerId: string;
  idArchive?: string;
  allDisabled: boolean;
}

export interface FormValues {
  [key: string]: any;

  isAutoFilling?: boolean;
  mainAddressreestrAddress?: string;
  mainAddresscountry?: { label: string; value: number };
  mainAddressfo?: { label: string; value: number };
  mainAddresssubject?: { label: string; value: number };
  mainAddressdistrict?: { label: string; value: number };
  mainAddresscity?: { label: string; value: number };
  mainAddresslocality?: { label: string; value: number };
  mainAddressstreet?: { label: string; value: number };
  mainAddresshouse?: { label: string; value: number };
  mainAddressindex?: string;
  mainAddresslatitude?: string;
  mainAddresslongitude?: string;
  mainAddressfullAddress?: string;
  mainAddressoffice?: string;
  xwebSaleAddressfullAddress?: string;
  xwebSaleAddressoffice?: string;
  xwebStoAddressfullAddress?: string;
  xwebStoAddressoffice?: string;
  legalAddresscountry?: { label: string; value: number };
  legalAddressfo?: { label: string; value: number };
  legalAddresssubject?: { label: string; value: number };
  legalAddressdistrict?: { label: string; value: number };
  legalAddresscity?: { label: string; value: number };
  legalAddresslocality?: { label: string; value: number };
  legalAddressstreet?: { label: string; value: number };
  legalAddresshouse?: { label: string; value: number };
  legalAddressindex?: string;
  legalAddressoffice?: string;
  legalAddresslatitude?: string;
  legalAddresslongitude?: string;
  legalAddressfullAddress?: string;
  legalAddressreestrLegalAddress?: string;
  postAddresscountry?: { label: string; value: number };
  postAddressfo?: { label: string; value: number };
  postAddresssubject?: { label: string; value: number };
  postAddressdistrict?: { label: string; value: number };
  postAddresscity?: { label: string; value: number };
  postAddresslocality?: { label: string; value: number };
  postAddressreestrPostAddress?: string;
  postAddressoffice?: string;
  aspAddresscountry?: { label: string; value: number };
  aspAddressfo?: { label: string; value: number };
  aspAddresssubject?: { label: string; value: number };
  aspAddressdistrict?: { label: string; value: number };
  aspAddresscity?: { label: string; value: number };
  aspAddresslocality?: { label: string; value: number };
  aspAddressstreet?: { label: string; value: number };
  aspAddresshouse?: { label: string; value: number };
  aspAddressindex?: string;
  aspAddressoffice?: string;
  aspAddresslatitude?: string;
  aspAddresslongitude?: string;
  aspAddressfullAddress?: string;
}

const Address: FC<AddressProps> = ({ DealerId, idArchive, allDisabled }) => {
  const { t } = useTranslation();
  const isRu = localStorage.getItem("language") === "ru";
  const { hasPermission, loading: loadingPermissions } = usePermissions();
  const {
    data: dataDealer,
    isFetching: isLoadingDealer,
    refetch,
  } = useGetDealerAddress(DealerId);
  const { mutate: updateAddress, isLoading: isUpdating } = useUpdateAddress();
  const [addNotificationMutation] = useAddNotificationMutation({});
  const [aspCount, setAspCount] = useState<number>(0);
  const [aspList, setAspList] = useState<number[]>([]);
  const { coordinatesList } = useContext<CoordinatesContextProps>(
    GetCoordinatesContext,
  );

  const method = useForm<FormValues>({
    defaultValues: {
      isAutoFilling: false,
    },
  });

  const normalizeDataDealer = (data: any) => {
    const mapAddressToForm = (address: any, prefix = "", info?: boolean) => {
      if (!address) return {};
      const mapField = (field: any, isSocr = true) =>
        field
          ? {
              label: field.name + " " + `${isSocr ? field.socr : ""}`,
              value: field.id,
            }
          : null;

      const dataItem = {
        [`${prefix}country`]: mapField(address.country),
        [`${prefix}fo`]: mapField(address.fo),
        [`${prefix}subject`]: mapField(address.subject),
        [`${prefix}district`]: mapField(address.district),
        [`${prefix}city`]: mapField(address.city),
        [`${prefix}locality`]: mapField(address.locality),
        [`${prefix}street`]: mapField(address.street),
        [`${prefix}house`]: mapField(address.house, false),
        [`${prefix}index`]: address.index ?? "",
        [`${prefix}office`]: address.office ?? "",
        [`${prefix}latitude`]: address.latitude ?? "",
        [`${prefix}longitude`]: address.longitude ?? "",
        [`${prefix}fullAddress`]: "",
        [`${prefix}reestrAddress`]: address.reestrAddress ?? "",
        [`${prefix}reestrLegalAddress`]: address.reestrLegalAddress ?? "",
        [`${prefix}reestrPostAddress`]: address.reestrPostAddress ?? "",
      };
      if (info) {
        dataItem[`${prefix}name`] = address.name ?? "";
        dataItem[`${prefix}phoneCode`] = address.phoneCode ?? "";
        dataItem[`${prefix}phone`] = address.phone ?? "";
        dataItem[`${prefix}email`] = address.email ?? "";
      }
      return dataItem;
    };

    const processAspAddresses = (aspArray: any[] | null) => {
      if (!aspArray || !Array.isArray(aspArray)) {
        return {};
      }
      let aspAddresses: any = {};

      aspArray.forEach((asp, index) => {
        aspAddresses = {
          ...aspAddresses,
          ...mapAddressToForm(asp, `${index + 1}-aspAddress`, true),
          [`${index + 1}-aspAddressinn`]: asp.inn ?? "",
          [`${index + 1}-aspAddresslegalName`]: asp.legalName ?? "",
        };
      });
      return aspAddresses;
    };

    return {
      ...mapAddressToForm(data?.main, "mainAddress"),
      ...mapAddressToForm(data?.additional?.legalAddress, "legalAddress"),
      ...mapAddressToForm(data?.additional?.postAddress, "postAddress"),
      ...mapAddressToForm(data?.xweb?.sto, "xwebStoAddress", true),
      ...mapAddressToForm(data?.xweb?.sale, "xwebSaleAddress", true),
      ...mapAddressToForm(data?.xweb?.sale),

      xwebPhoneCode: data?.xweb?.phoneCode ?? "",
      xwebPhone: data?.xweb?.phone ?? "",
      xwebName: data?.xweb?.name ?? "",
      xwebXshop: !data?.xweb?.xshop ? "Нет" : "Да",
      xwebEmail: data?.xweb?.email ?? "",
      ...mapAddressToForm(data?.lada?.sto, "ladaStoAddress", true),
      ...mapAddressToForm(data?.lada?.sale, "ladaSaleAddress", true),
      ladaPhoneCode: data?.lada?.phoneCode ?? "",
      ladaPhone: data?.lada?.phone ?? "",
      ladaEmail: data?.lada?.email ?? "",
      ...processAspAddresses(data?.asp),
    };
  };

  // const formValues = useMemo(() => {
  //   if (!dataDealer) return {};
  //   return normalizeDataDealer(dataDealer);
  // }, [dataDealer]);

  const prevDataRef = useRef<string | null>(null);

  useEffect(() => {
    if (!dataDealer) return;
    const normalized = normalizeDataDealer(dataDealer);
    const currentValues = method.getValues();
    const normalizedString = JSON.stringify(normalized);

    if (prevDataRef.current === normalizedString) return;

    const isSame = JSON.stringify(currentValues) === normalizedString;
    if (!isSame) {
      method.reset(normalized);
      prevDataRef.current = normalizedString;
    }
  }, [dataDealer, method]);

  useEffect(() => {
    const shadow = method.getValues();

    Object.keys(coordinatesList).map((item) => {
      if (item in shadow) {
        method.setValue(item, coordinatesList[item]);
      }
    });
  }, [method, coordinatesList]);

  const canSend = (permissions: string[]) =>
    permissions.some((p) => hasPermission(p));

  const filterTransformedByPermissions = (data) => {
    const result = { id: data.id };

    // Общий permission — если есть, отправляем всё без фильтрации
    if (hasPermission("dealer.updateAddress")) {
      return { ...data };
    }

    // main
    if (hasPermission("dealer.updateAddress.main")) {
      result.main = data.main;
    }

    // additional
    if (data.additional) {
      const allowedAdditional = {};

      if (hasPermission("dealer.updateAddress.legal")) {
        allowedAdditional.legalAddress = data.additional.legalAddress;
      }
      if (hasPermission("dealer.updateAddress.post")) {
        allowedAdditional.postAddress = data.additional.postAddress;
      }

      if (Object.keys(allowedAdditional).length > 0) {
        result.additional = allowedAdditional;
      }
    }

    // xweb
    if (data.xweb) {
      const allowedXweb = {};

      if (hasPermission("dealer.updateAddress.xweb.sto")) {
        allowedXweb.sto = data.xweb.sto;
      }
      if (hasPermission("dealer.updateAddress.xweb.sale")) {
        allowedXweb.sale = data.xweb.sale;
      }

      // специальное правило — xshop даёт доступ и к name
      if (hasPermission("dealer.updateAddress.xweb")) {
        allowedXweb.xshop = data.xweb.xshop;
        allowedXweb.name = data.xweb.name;
      }

      if (Object.keys(allowedXweb).length > 0) {
        result.xweb = allowedXweb;
      }
    }

    // lada
    if (data.lada) {
      const allowedLada = {};

      if (hasPermission("dealer.updateAddress.lada.sto")) {
        allowedLada.sto = data.lada.sto;
      }
      if (hasPermission("dealer.updateAddress.lada.sale")) {
        allowedLada.sale = data.lada.sale;
      }

      if (Object.keys(allowedLada).length > 0) {
        result.lada = allowedLada;
      }
    }

    // asp
    if (data.asp && hasPermission("dealer.updateAddress.asp")) {
      result.asp = data.asp;
    }

    return result;
  };

  const validateAspInn = (data: any) => {
    let isValid = true;
    aspList.forEach((index) => {
      const key = `${index}-aspAddressinn`;
      const digits = data[key] ? String(data[key]).replace(/\D/g, "") : "";
      if (digits && digits.length !== 10) {
        method.setError(key, {
          type: "validate",
          message: t("validation.invalid_inn_legal"),
        });
        isValid = false;
      } else {
        method.clearErrors(key);
      }
    });
    return isValid;
  };

  const onSubmit = (data: any) => {
    if (!validateAspInn(data)) return;

    const houseKeys = Object.keys(data).filter((el) => /house/.test(el));
    // const errorList = resolveIsHouseFieldComplete(
    //   data,
    //   houseKeys,
    //   method.setError,
    //   t
    // );
    // if (!errorList) {

    const main = transformData(data, Number(DealerId), aspList);

    const filteredMain = filterTransformedByPermissions(main);

    updateAddress(
      { dealerId: DealerId, data: { ...filteredMain } },
      {
        onSuccess: () => {
          addNotificationMutation({
            variables: {
              type: NOTIFICATIONS.success,
              content: t("common.сhange_was_successful"),
            },
          });
          refetch();
        },
      },
    );
    // }
  };

  const autoFillAddress = async (prefix: string) => {
    method.setValue("isAutoFilling", true, { shouldValidate: false });

    for (const field of Object.keys(addressFields)) {
      const sourceValue = method.getValues(`mainAddress${field}`);
      method.setValue(`${prefix}${field}`, sourceValue, {
        shouldValidate: false,
      });

      await new Promise((resolve) => setTimeout(resolve, 0));
    }

    const additionalFields = [
      "index",
      "latitude",
      "longitude",
      "fullAddress",
      "office",
    ];
    for (const field of additionalFields) {
      const sourceValue = method.getValues(`mainAddress${field}`);
      if (sourceValue !== undefined && sourceValue !== null) {
        method.setValue(`${prefix}${field}`, sourceValue, {
          shouldValidate: false,
        });
        await new Promise((resolve) => setTimeout(resolve, 0));
      }
    }

    await new Promise((resolve) => setTimeout(resolve, 100));
    method.setValue("isAutoFilling", false, { shouldValidate: false });
  };

  const updateFieldConfig = (fields: any[]) =>
    fields.map((field) => ({
      ...field,
      defaultValue: method.getValues(field.name) ?? field.defaultValue ?? "",
    }));

  const dealerFields = useMemo(
    () => updateFieldConfig(getDealerFields(t, "mainAddress")),
    [t, method],
  );
  const dealerCoordinats = useMemo(
    () => updateFieldConfig(getDealerCoordinats(t, "mainAddress")),
    [t, method],
  );
  const dealerCoordinatsXwebSto = useMemo(
    () => updateFieldConfig(getDealerCoordinats(t, "xwebStoAddress")),
    [t, method],
  );
  const dealerCoordinatsXwebSale = useMemo(
    () => updateFieldConfig(getDealerCoordinats(t, "xwebSaleAddress")),
    [t, method],
  );

  const dealerInfoLegal = useMemo(
    () => updateFieldConfig(getDealerInfoLegal(t, "legalAddress")),
    [t, method],
  );
  const dealerInfoPost = useMemo(
    () => updateFieldConfig(getDealerInfoLegal(t, "postAddress")),
    [t, method],
  );

  const dealerInfoXwebSto = useMemo(
    () => updateFieldConfig(getDealerInfoMail(t, "xwebStoAddress")),
    [t, method],
  );
  const dealerInfoXwebSale = useMemo(
    () => updateFieldConfig(getDealerInfoMail(t, "xwebSaleAddress")),
    [t, method],
  );
  const dealerInfoMail = useMemo(
    () => updateFieldConfig(getDealerInfoMail(t, "xweb")),
    [t, method],
  );
  const dealerInfoNameXweb = useMemo(
    () => updateFieldConfig(getDealerXshop(t, "xweb")),
    [t, method],
  );

  const dealerCoordinatsLadaSto = useMemo(
    () => updateFieldConfig(getDealerCoordinats(t, "ladaStoAddress")),
    [t, method],
  );
  const dealerCoordinatsLadaSale = useMemo(
    () => updateFieldConfig(getDealerCoordinats(t, "ladaSaleAddress")),
    [t, method],
  );

  const dealerInfoLadaSto = useMemo(
    () => updateFieldConfig(getDealerInfoMail(t, "ladaStoAddress")),
    [t, method],
  );
  const dealerInfoLadaSale = useMemo(
    () => updateFieldConfig(getDealerInfoMail(t, "ladaSaleAddress")),
    [t, method],
  );
  const dealerInfoLadaMail = useMemo(
    () => updateFieldConfig(getDealerInfoMail(t, "lada")),
    [t, method],
  );
  const dealerFieldsAsp = useMemo(
    () => updateFieldConfig(getDealerFields(t, "aspAddress")),
    [t, method],
  );
  const dealerInfoAsp = useMemo(
    () => updateFieldConfig(getDealerInfoAsp(t, "aspAddress")),
    [t, method],
  );
  const dealerCoordinatsAsp = useMemo(
    () => updateFieldConfig(getDealerCoordinats(t, "aspAddress")),
    [t, method],
  );

  const addAspAddress = () => {
    setAspList((prev) => [...prev, prev.length ? Math.max(...prev) + 1 : 1]);
  };

  const removeAspAddress = (index: number) => {
    setAspList((prev) => prev.filter((i) => i !== index));
    const aspFields = [
      "country",
      "fo",
      "subject",
      "district",
      "city",
      "locality",
      "street",
      "house",
      "index",
      "office",
      "latitude",
      "longitude",
      "fullAddress",
      "name",
      "inn",
      "legalName",
      "phoneCode",
      "phone",
      "email",
    ];
    aspFields.forEach((field) => {
      method.setValue(`${index}-aspAddress${field}`, undefined, {
        shouldValidate: false,
      });
    });
  };

  useEffect(() => {
    if (dataDealer?.asp?.length > 0) {
      setAspList(dataDealer.asp.map((_, i) => i + 1));
    }
  }, [dataDealer?.asp?.length]);

  const canSave =
    allDisabled || permissionSaveButton.some((key) => hasPermission(key));
  if (isLoadingDealer) {
    return <div>Загрузка...</div>;
  }

  return (
    <div>
      <FormProvider {...method}>
        <form
          onSubmit={method.handleSubmit(onSubmit)}
          className={clsx("dynamic-form")}
        >
          <div className={s.wrapper}>
            <div className={s.wrapperItem}>
              <MainAddress
                isRu={isRu}
                dealerCoordinats={dealerCoordinats}
                allDisabled={
                  !(allDisabled || hasPermission("dealer.updateAddress.main"))
                }
              />
              <AdditionalAddress
                isRu={isRu}
                autoFillAddress={autoFillAddress}
                allDisabled={allDisabled}
              />
              <NetxAddress
                isRu={isRu}
                autoFillAddress={autoFillAddress}
                dealerInfoNameXweb={dealerInfoNameXweb}
                dealerCoordinatsXwebSto={dealerCoordinatsXwebSto}
                dealerInfoXwebSto={dealerInfoXwebSto}
                dealerCoordinatsXwebSale={dealerCoordinatsXwebSale}
                dealerInfoXwebSale={dealerInfoXwebSale}
                allDisabled={allDisabled}
              />
              <LadaAddress
                isRu={isRu}
                autoFillAddress={autoFillAddress}
                dealerCoordinatsLadaSto={dealerCoordinatsLadaSto}
                dealerInfoLadaSto={dealerInfoLadaSto}
                dealerCoordinatsLadaSale={dealerCoordinatsLadaSale}
                dealerInfoLadaSale={dealerInfoLadaSale}
                allDisabled={allDisabled}
              />

              <div className={s.aspAddressWrapper}>
                <AspAddress
                  dealerInfoAsp={dealerInfoAsp}
                  addAspAddress={addAspAddress}
                  aspList={aspList}
                  isRu={isRu}
                  allDisabled={
                    !(allDisabled || hasPermission("dealer.updateAddress.asp"))
                  }
                  dealerCoordinatsAsp={dealerCoordinatsAsp}
                  autoFillAddress={autoFillAddress}
                  removeAspAddress={removeAspAddress}
                />
              </div>
            </div>
            <div className={s.wrapperBTN}>
              {canSave && (
                <Button
                  txtBtn={"Сохранить изменения"}
                  orange
                  isForm
                  customClassName={s.but}
                />
              )}
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default memo(Address);
